import React from "react";
import PropTypes from "prop-types";
import Button from "components/Common/Button";
import SvgIcon from "components/Common/SvgIcon";
import * as Immutable from "immutable";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import { BillingPlanRecord } from "reducers/billingPlan/reducer";
import { Client } from "resourceModels";
import { getResource } from "selectors/resources";
import pluralize from "services/pluralize";
import convertCentsToDollars from "services/money-converter";
import colors from "stylesheets/utilities/colors.scss";
import { patchBillingPlan } from "./actions";
import "./style.scss";

class ModalBillingPlans extends React.Component {
  handleInputChange(field, value) {
    const objectToMerge = {};
    objectToMerge[field] = value;

    this.props.setState("MODAL_BILLING_PLANS", objectToMerge);
  }

  handleMakeRequest(selectedPlanIsEmptyCustom) {
    const {
      /** @type Client */
      client
    } = this.props;

    if (selectedPlanIsEmptyCustom) {
      window.location.href = `mailto:hello@ada.support?Subject=Custom Pricing for ${client.name}&body=Hi Ada Team,%0D%0A%0D%0ALet’s set up a custom plan for ${window.location.hostname}`;
    } else {
      this.props.patchBillingPlan(this.props.modalBillingPlans);
    }
  }

  handleColumnClick(tier) {
    const { billingPlanRecord } = this.props;

    let clickedPlan = billingPlanRecord.availablePlans.find(plan => plan.get("tier") === tier);

    if (tier === "custom" && !clickedPlan) {
      clickedPlan = Immutable.fromJS({
        tier: "custom"
      });
    }

    this.handleInputChange("plan", clickedPlan);
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      /** @type Client */
      client,
      billingPlanRecord,
      billingPlanRecord: { availablePlans }
    } = this.props;

    const trialPlan = availablePlans.find(plan => plan.get("tier") === "trial");
    const proPlan = availablePlans.find(plan => plan.get("tier") === "pro");
    const customPlan = availablePlans.find(plan => plan.get("tier") === "custom");
    const selectedPlanIsTrial = this.props.selectedPlan.get("tier") === "trial";
    const selectedPlanIsEmptyCustom = !customPlan && this.props.selectedPlan.get("tier") === "custom";

    const tiersWithFeatures = [
      {
        tier: "trial",
        label: trialPlan.get("name"),
        price: "Free",
        features: [
          "Internal testing only",
          "No public conversations",
          "",
          "",
          ""
        ]
      },
      {
        tier: "pro",
        label: proPlan.get("name"),
        price: proPlan && `$${convertCentsToDollars(proPlan.get("amount"), false)}`,
        features: [
          "Publish to any platform",
          proPlan && `${proPlan.get("conversationLimit")} ${pluralize(proPlan.get("conversationLimit"), "conversation")}*`,
          "History & analytics",
          "Email support",
          ""
        ]
      },
      {
        tier: "custom",
        label: customPlan ? customPlan.get("name") : "Custom Plan",
        price: customPlan ? `$${convertCentsToDollars(customPlan.get("amount"), false)}` : "Inquire",
        features: [
          "Custom platforms",
          customPlan ? `${customPlan.get("conversationLimit")} ${pluralize(customPlan.get("conversationLimit"), "conversation")}` : "∞ conversations",
          "Phone/Slack support",
          "Training services",
          "Performance monitoring"
        ]
      }
    ];

    let buttonText;
    let buttonIcon;
    let subText;
    let subTextAlignment;

    if (selectedPlanIsTrial) {
      buttonText = "Downgrade";
      buttonIcon = "Downward";
    } else if (selectedPlanIsEmptyCustom) {
      buttonText = "Send Inquiry Email";
      buttonIcon = "Send";
    } else {
      buttonText = `Purchase ${this.props.selectedPlan.get("name")}`;
      buttonIcon = "Lock";
    }


    if (selectedPlanIsTrial && this.props.currentPlan.get("tier") !== "trial") {
      subText = "Yikes! Downgrading to a trial plan will prevent your bot from answering questions outside of your team.";
      subTextAlignment = "ModalBillingPlans__bottom__price-container__sub-text--align_left";
    } else if (billingPlanRecord.card && !selectedPlanIsEmptyCustom) {
      subText = "Billed monthly";
      subTextAlignment = "ModalBillingPlans__bottom__price-container__sub-text--align-right";
    }

    return (
      <div className="ModalBillingPlans Modal__modal">
        <h5 className="Modal__title">
          Monthly Plans
        </h5>
        <div className="Modal__content">
          <section className="ModalBillingPlans__section g-form__block">
            <div className="ModalBillingPlans__section__input-container">
              <div className="ModalBillingPlans__section__input-container__input">
                <div className="ModalBillingPlans__table">
                  {tiersWithFeatures.map((tier) => {
                    const isActivePlan = client.tier === tier.tier;
                    const isSelectedPlan = this.props.selectedPlan.get("tier") === tier.tier;

                    return (
                      // Not going to have a keyboard handler
                      <div // eslint-disable-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus, max-len
                        role="button"
                        key={tier}
                        className={`
                                                    ModalBillingPlans__table__column
                                                    ${isSelectedPlan ? "ModalBillingPlans__table__column--selected" : ""}
                                                `}
                        onClick={this.handleColumnClick.bind(this, tier.tier)}
                      >
                        <div className="ModalBillingPlans__table__column__head">
                          {isActivePlan && (
                            <SvgIcon
                              icon="CircleCheckmark"
                              fillColor={colors.colorUIPrimary}
                              customClassName="ModalBillingPlans__table__column__head__icon"
                            />
                          )}
                          {tier.label}
                        </div>
                        <div className="ModalBillingPlans__table__column__features">
                          {tier.features.map(feature => (
                            <div key={`${tier}-${feature}`} className="ModalBillingPlans__table__column__features__feature">
                              {feature}
                            </div>
                          ))}
                        </div>
                        <div className="ModalBillingPlans__table__column__footer">
                          {tier.price}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <p className="ModalBillingPlans__section__description g-form__block__description">
              *Worry not! If we find you’re exceeding 10,000 conversations often,{" "}
              we’ll work with you to set up a custom plan that allots for your needs.
            </p>
          </section>
        </div>
        <div
          className={`
            Modal__bottom ModalBillingPlans__bottom
            ${this.props.selectedPlan.get("planId") === this.props.currentPlan.get("planId") ? "ModalBillingPlans__bottom--hidden" : ""}
          `}
        >
          <Button
            title={buttonText}
            text={buttonText}
            icon={buttonIcon}
            fillColor={selectedPlanIsTrial ? colors.colorTextGrey : colors.colorWhite}
            customClassName="ModalBillingPlans__bottom__button"
            onClick={() => this.handleMakeRequest(selectedPlanIsEmptyCustom)}
            light={selectedPlanIsTrial}
            disabled={
              this.props.modalBillingPlans.get("isLoading") ||
              this.props.selectedPlan.get("planId") === this.props.currentPlan.get("planId")
            }
            isLoading={this.props.modalBillingPlans.get("isLoading")}
          />
          <div className="ModalBillingPlans__bottom__price-container">
            {!selectedPlanIsEmptyCustom && !selectedPlanIsTrial && (
              <div className="ModalBillingPlans__bottom__price-container__price">
                ${convertCentsToDollars(this.props.selectedPlan.get("amount"))}
                <span className="ModalBillingPlans__bottom__price-container__price__currency">
                  ({this.props.selectedPlan.get("currency")})
                </span>
              </div>
            )}
            {Boolean(subText) && (
              <div
                className={
                  `ModalBillingPlans__bottom__price-container__sub-text ${subTextAlignment}`
                }
              >
                {subText}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

ModalBillingPlans.propTypes = {
  client: PropTypes.instanceOf(Client).isRequired,
  billingPlanRecord: PropTypes.instanceOf(BillingPlanRecord).isRequired
};

function mapState(state) {
  const { modalBillingPlans, billingPlan } = state;

  return {
    client: getResource(state, "client"),
    modalBillingPlans,
    billingPlanLoading: billingPlan.get("isLoading"),
    billingPlanRecord: billingPlan.get("record"),
    selectedPlan: modalBillingPlans.get("plan"),
    currentPlan: billingPlan.get("record").availablePlans.find(plan => (
      plan.get("planId") === billingPlan.get("record").subscription.get("planId")
    ))
  };
}

function mapDispatch(dispatch) {
  return {
    patchBillingPlan: bindActionCreators(patchBillingPlan, dispatch)
  };
}

const Connector = connect(mapState, mapDispatch)(ModalBillingPlans);

export default Connector;
